import {
  DIRECTIONS,
  START_TILE_ID,
  TILE_CATALOG,
  type Direction,
  type FeatureType,
  type Rotation,
  type TileId,
  playableTileDeck,
  rotatedEdges,
  rotatedRegionEdges,
} from "./catalog.js";

export const LUNA_RULES_VERSION = "1.0.0";
export const TURN_TIMEOUT_MS = 48 * 60 * 60 * 1000;
export const COLONISTS_PER_PLAYER = 7;
export const ROTATIONS: readonly Rotation[] = [0, 90, 180, 270];

export interface Coordinate { x: number; y: number }
export interface Placement extends Coordinate { rotation: Rotation }
export interface PlacedTile extends Placement { tileId: TileId }
export interface LunaPlayer {
  playerId: string;
  score: number;
  colonists: number;
  completedColonies: number;
  forfeited: boolean;
}

export interface LunaClaim { playerId: string; node: string }
export type LunaPhase = "waiting_for_players" | "playing" | "complete";

export interface LunaState {
  schemaVersion: 1;
  rulesVersion: string;
  roomId: string;
  capacity: number;
  phase: LunaPhase;
  players: LunaPlayer[];
  board: Record<string, PlacedTile>;
  claims: LunaClaim[];
  settledFeatures: string[];
  tileStack: TileId[];
  currentTile: TileId | null;
  discardedTiles: TileId[];
  activePlayerIndex: number;
  deadline: number | null;
  revision: number;
  winnerIds: string[];
  finalReason: "tiles_exhausted" | "forfeit" | "expired" | null;
}

export interface LegalPlacement extends Placement {}

export interface ClaimOption {
  regionId: string;
  type: FeatureType;
  label: string;
}

export interface LunaView {
  schemaVersion: 1;
  rulesVersion: string;
  roomId: string;
  capacity: number;
  phase: LunaPhase;
  players: LunaPlayer[];
  you: string;
  board: Record<string, PlacedTile>;
  claims: LunaClaim[];
  currentTile: TileId | null;
  discardedTiles: TileId[];
  tilesRemaining: number;
  activePlayerId: string | null;
  deadline: number | null;
  revision: number;
  winnerIds: string[];
  finalReason: LunaState["finalReason"];
  legalPlacements: LegalPlacement[];
}

export type LunaCommand =
  | {
    type: "commit_turn";
    commandId: string;
    expectedRevision: number;
    placement: Placement;
    claimRegionId?: string;
  }
  | {
    type: "resign";
    commandId: string;
    expectedRevision: number;
  };

export interface ScoreEvent {
  featureType: FeatureType;
  points: number;
  playerIds: string[];
  completed: boolean;
}

export interface LunaTransition {
  state: LunaState;
  scores: ScoreEvent[];
  discarded: TileId[];
}

interface FeatureComponent {
  type: FeatureType;
  nodes: string[];
  tileKeys: string[];
  complete: boolean;
  launchpads: number;
}
type BoardFeatureState = Pick<LunaState, "board" | "claims">;

const DELTAS: Readonly<Record<Direction, Coordinate>> = {
  N: { x: 0, y: -1 }, E: { x: 1, y: 0 }, S: { x: 0, y: 1 }, W: { x: -1, y: 0 },
};
const OPPOSITE: Readonly<Record<Direction, Direction>> = { N: "S", E: "W", S: "N", W: "E" };

export const coordinateKey = (x: number, y: number): string => `${x},${y}`;
export function parseCoordinate(key: string): Coordinate {
  const [x, y] = key.split(",").map(Number);
  if (!Number.isInteger(x) || !Number.isInteger(y)) throw new Error("invalid_coordinate");
  return { x: x!, y: y! };
}
const nodeKey = (tileKey: string, regionId: string): string => `${tileKey}|${regionId}`;
function parseNode(node: string): { tileKey: string; regionId: string } {
  const split = node.lastIndexOf("|");
  if (split < 1) throw new Error("invalid_feature_node");
  return { tileKey: node.slice(0, split), regionId: node.slice(split + 1) };
}

function cloneState(state: LunaState): LunaState {
  return structuredClone(state);
}

export function createLunaState(roomId: string, hostId: string, capacity: number): LunaState {
  if (!roomId || !hostId) throw new Error("invalid_room");
  if (!Number.isInteger(capacity) || capacity < 2 || capacity > 5) throw new Error("invalid_capacity");
  return {
    schemaVersion: 1,
    rulesVersion: LUNA_RULES_VERSION,
    roomId,
    capacity,
    phase: "waiting_for_players",
    players: [newPlayer(hostId)],
    board: { "0,0": { tileId: START_TILE_ID, x: 0, y: 0, rotation: 0 } },
    claims: [],
    settledFeatures: [],
    tileStack: [],
    currentTile: null,
    discardedTiles: [],
    activePlayerIndex: 0,
    deadline: null,
    revision: 0,
    winnerIds: [],
    finalReason: null,
  };
}

function newPlayer(playerId: string): LunaPlayer {
  return { playerId, score: 0, colonists: COLONISTS_PER_PLAYER, completedColonies: 0, forfeited: false };
}

export function addLunaPlayer(state: LunaState, playerId: string): LunaState {
  const next = cloneState(state);
  if (next.players.some((player) => player.playerId === playerId)) return next;
  if (next.phase !== "waiting_for_players") throw new Error("game_already_started");
  if (next.players.length >= next.capacity) throw new Error("room_full");
  next.players.push(newPlayer(playerId));
  next.revision += 1;
  return next;
}

/** Starts a full room with server-shuffled player and tile order. */
export function startLunaGame(state: LunaState, playerOrder: string[], tileOrder: TileId[], now: number): LunaState {
  const next = cloneState(state);
  if (next.phase !== "waiting_for_players" || next.players.length !== next.capacity) throw new Error("room_not_ready");
  if (playerOrder.length !== next.capacity || new Set(playerOrder).size !== next.capacity || playerOrder.some((id) => !next.players.some((p) => p.playerId === id))) {
    throw new Error("invalid_player_order");
  }
  const expected = playableTileDeck().sort();
  if (tileOrder.length !== expected.length || tileOrder.slice().sort().some((id, index) => id !== expected[index])) throw new Error("invalid_tile_order");
  const byId = new Map(next.players.map((player) => [player.playerId, player]));
  next.players = playerOrder.map((id) => byId.get(id)!);
  next.tileStack = [...tileOrder];
  next.phase = "playing";
  next.activePlayerIndex = 0;
  next.deadline = now + TURN_TIMEOUT_MS;
  next.revision += 1;
  drawPlaceableTile(next);
  return next;
}

export function activePlayerId(state: LunaState): string | null {
  return state.phase === "playing" ? state.players[state.activePlayerIndex]?.playerId ?? null : null;
}

export function lunaView(state: LunaState, playerId: string): LunaView {
  if (!state.players.some((player) => player.playerId === playerId)) throw new Error("forbidden");
  return {
    schemaVersion: state.schemaVersion,
    rulesVersion: state.rulesVersion,
    roomId: state.roomId,
    capacity: state.capacity,
    phase: state.phase,
    players: structuredClone(state.players),
    you: playerId,
    board: structuredClone(state.board),
    claims: structuredClone(state.claims),
    currentTile: state.currentTile,
    discardedTiles: [...state.discardedTiles],
    tilesRemaining: state.tileStack.length,
    activePlayerId: activePlayerId(state),
    deadline: state.deadline,
    revision: state.revision,
    winnerIds: [...state.winnerIds],
    finalReason: state.finalReason,
    legalPlacements: state.phase === "playing" && state.currentTile && activePlayerId(state) === playerId ? legalPlacements(state, state.currentTile) : [],
  };
}

export function isLegalPlacement(state: Pick<LunaState, "board">, tileId: TileId, placement: Placement): boolean {
  if (!ROTATIONS.includes(placement.rotation) || !Number.isInteger(placement.x) || !Number.isInteger(placement.y)) return false;
  const target = coordinateKey(placement.x, placement.y);
  if (state.board[target]) return false;
  const edges = rotatedEdges(tileId, placement.rotation);
  let touches = false;
  for (const direction of DIRECTIONS) {
    const delta = DELTAS[direction];
    const neighbor = state.board[coordinateKey(placement.x + delta.x, placement.y + delta.y)];
    if (!neighbor) continue;
    touches = true;
    const neighborEdges = rotatedEdges(neighbor.tileId, neighbor.rotation);
    if (edges[direction] !== neighborEdges[OPPOSITE[direction]]) return false;
  }
  return touches;
}

export function legalPlacements(state: Pick<LunaState, "board">, tileId: TileId): LegalPlacement[] {
  const frontier = new Map<string, Coordinate>();
  for (const tile of Object.values(state.board)) {
    for (const direction of DIRECTIONS) {
      const delta = DELTAS[direction];
      const candidate = { x: tile.x + delta.x, y: tile.y + delta.y };
      const key = coordinateKey(candidate.x, candidate.y);
      if (!state.board[key]) frontier.set(key, candidate);
    }
  }
  const legal: LegalPlacement[] = [];
  for (const candidate of frontier.values()) {
    for (const rotation of ROTATIONS) {
      const placement = { ...candidate, rotation };
      if (isLegalPlacement(state, tileId, placement)) legal.push(placement);
    }
  }
  return legal;
}

export function claimOptionsForPlacement(state: BoardFeatureState, tileId: TileId, placement: Placement): ClaimOption[] {
  if (!isLegalPlacement(state, tileId, placement)) return [];
  const preview: BoardFeatureState = { board: structuredClone(state.board), claims: structuredClone(state.claims) };
  const key = coordinateKey(placement.x, placement.y);
  preview.board[key] = { tileId, ...placement };
  return TILE_CATALOG[tileId].regions.flatMap((region) => {
    const component = componentFromNode(preview, nodeKey(key, region.id));
    if (claimsFor(preview, component).length > 0) return [];
    return [{
      regionId: region.id,
      type: region.type,
      label: region.type === "tube" ? "Transport tube" : region.type === "colony" ? (region.launchpad ? "Launch pad colony" : "Colony") : "Solar array",
    }];
  });
}

export function applyLunaCommand(state: LunaState, actorId: string, command: LunaCommand, now: number): LunaTransition {
  if (!command.commandId || command.commandId.length > 80) throw new Error("invalid_command_id");
  if (command.expectedRevision !== state.revision) throw new Error("stale_revision");
  if (state.phase !== "playing") throw new Error("game_not_playing");
  const next = cloneState(state);
  if (!next.players.some((player) => player.playerId === actorId && !player.forfeited)) throw new Error("forbidden");
  if (command.type === "resign") {
    forfeitPlayerInPlace(next, actorId, now, "forfeit");
    return { state: next, scores: [], discarded: [] };
  }
  if (activePlayerId(next) !== actorId) throw new Error("not_your_turn");
  if (!next.currentTile) throw new Error("no_current_tile");
  if (!isLegalPlacement(next, next.currentTile, command.placement)) throw new Error("illegal_placement");

  const tileId = next.currentTile;
  const tileKey = coordinateKey(command.placement.x, command.placement.y);
  next.board[tileKey] = { tileId, ...command.placement };
  if (command.claimRegionId !== undefined) {
    const player = next.players[next.activePlayerIndex]!;
    if (player.colonists < 1) throw new Error("no_colonists");
    const option = claimOptionsOnPlacedTile(next, tileKey, command.claimRegionId);
    if (!option) throw new Error("feature_already_claimed");
    next.claims.push({ playerId: actorId, node: nodeKey(tileKey, command.claimRegionId) });
    player.colonists -= 1;
  }

  const scores = resolveNewCompletions(next, tileKey);
  next.currentTile = null;
  next.revision += 1;
  if (next.tileStack.length === 0) {
    finishByTiles(next, scores);
    return { state: next, scores, discarded: [] };
  }
  advanceActivePlayer(next);
  next.deadline = now + TURN_TIMEOUT_MS;
  const discarded = drawPlaceableTile(next);
  if (next.phase === "complete") finishByTiles(next, scores);
  return { state: next, scores, discarded };
}

function claimOptionsOnPlacedTile(state: LunaState, tileKey: string, regionId: string): ClaimOption | null {
  const tile = state.board[tileKey];
  const region = tile && TILE_CATALOG[tile.tileId].regions.find((candidate) => candidate.id === regionId);
  if (!tile || !region) throw new Error("unknown_region");
  const component = componentFromNode(state, nodeKey(tileKey, regionId));
  if (claimsFor(state, component).length > 0) return null;
  return { regionId, type: region.type, label: region.type };
}

function drawPlaceableTile(state: LunaState): TileId[] {
  const discarded: TileId[] = [];
  while (state.tileStack.length > 0) {
    const tileId = state.tileStack.shift()!;
    if (legalPlacements(state, tileId).length > 0) {
      state.currentTile = tileId;
      state.discardedTiles.push(...discarded);
      return discarded;
    }
    discarded.push(tileId);
  }
  state.discardedTiles.push(...discarded);
  state.currentTile = null;
  state.phase = "complete";
  return discarded;
}

function resolveNewCompletions(state: LunaState, placedTileKey: string): ScoreEvent[] {
  const candidates = new Set<string>();
  const tile = state.board[placedTileKey]!;
  for (const region of TILE_CATALOG[tile.tileId].regions) candidates.add(nodeKey(placedTileKey, region.id));
  const { x, y } = parseCoordinate(placedTileKey);
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    const nearbyKey = coordinateKey(x + dx, y + dy);
    const nearby = state.board[nearbyKey];
    if (!nearby) continue;
    for (const region of TILE_CATALOG[nearby.tileId].regions) if (region.type === "solar") candidates.add(nodeKey(nearbyKey, region.id));
  }
  const events: ScoreEvent[] = [];
  const seen = new Set<string>();
  for (const node of candidates) {
    const component = componentFromNode(state, node);
    const key = featureKey(component);
    if (seen.has(key) || state.settledFeatures.includes(key) || !component.complete) continue;
    seen.add(key);
    state.settledFeatures.push(key);
    const points = component.type === "tube" ? component.tileKeys.length
      : component.type === "colony" ? component.tileKeys.length * 2 + component.launchpads * 10
        : 9;
    events.push(awardFeature(state, component, points, true));
  }
  return events;
}

function awardFeature(state: LunaState, component: FeatureComponent, points: number, completed: boolean): ScoreEvent {
  const claims = claimsFor(state, component);
  const counts = new Map<string, number>();
  for (const claim of claims) counts.set(claim.playerId, (counts.get(claim.playerId) ?? 0) + 1);
  const majority = Math.max(0, ...counts.values());
  const winners = [...counts.entries()].filter(([, count]) => count === majority && majority > 0).map(([id]) => id);
  for (const playerId of winners) {
    const player = state.players.find((candidate) => candidate.playerId === playerId);
    if (!player) continue;
    player.score += points;
    if (completed && component.type === "colony") player.completedColonies += 1;
  }
  const nodes = new Set(component.nodes);
  state.claims = state.claims.filter((claim) => {
    if (!nodes.has(claim.node)) return true;
    const owner = state.players.find((candidate) => candidate.playerId === claim.playerId);
    if (owner) owner.colonists += 1;
    return false;
  });
  return { featureType: component.type, points, playerIds: winners, completed };
}

function finishByTiles(state: LunaState, events: ScoreEvent[]): void {
  if (state.finalReason !== null) return;
  const colonistsBeforeReturns = new Map(state.players.map((player) => [player.playerId, player.colonists]));
  const seen = new Set<string>();
  for (const [tileKey, tile] of Object.entries(state.board)) {
    for (const region of TILE_CATALOG[tile.tileId].regions) {
      const component = componentFromNode(state, nodeKey(tileKey, region.id));
      const key = featureKey(component);
      if (seen.has(key) || state.settledFeatures.includes(key)) continue;
      seen.add(key);
      const points = component.type === "solar"
        ? 1 + occupiedSolarNeighbors(state, tileKey)
        : component.tileKeys.length;
      events.push(awardFeature(state, component, points, false));
    }
  }
  state.phase = "complete";
  state.deadline = null;
  state.currentTile = null;
  state.finalReason = "tiles_exhausted";
  state.winnerIds = determineWinners(state, colonistsBeforeReturns);
}

function determineWinners(state: LunaState, colonistsBeforeReturns: Map<string, number>): string[] {
  const eligible = state.players.filter((player) => !player.forfeited);
  const bestScore = Math.max(...eligible.map((player) => player.score));
  let tied = eligible.filter((player) => player.score === bestScore);
  if (tied.length > 1) {
    const fewestSupply = Math.min(...tied.map((player) => colonistsBeforeReturns.get(player.playerId) ?? player.colonists));
    tied = tied.filter((player) => (colonistsBeforeReturns.get(player.playerId) ?? player.colonists) === fewestSupply);
  }
  if (tied.length > 1) {
    const mostColonies = Math.max(...tied.map((player) => player.completedColonies));
    tied = tied.filter((player) => player.completedColonies === mostColonies);
  }
  return tied.map((player) => player.playerId);
}

export function forfeitLunaPlayer(state: LunaState, playerId: string, now: number, reason: "forfeit" | "expired" = "forfeit"): LunaState {
  const next = cloneState(state);
  forfeitPlayerInPlace(next, playerId, now, reason);
  return next;
}

function forfeitPlayerInPlace(state: LunaState, playerId: string, now: number, reason: "forfeit" | "expired"): void {
  const index = state.players.findIndex((player) => player.playerId === playerId);
  const player = state.players[index];
  if (!player || player.forfeited) throw new Error("invalid_forfeit");
  player.forfeited = true;
  const removedClaims = state.claims.filter((claim) => claim.playerId === playerId).length;
  player.colonists += removedClaims;
  state.claims = state.claims.filter((claim) => claim.playerId !== playerId);
  state.revision += 1;
  const remaining = state.players.filter((candidate) => !candidate.forfeited);
  if (state.capacity === 2 || remaining.length <= 1) {
    state.phase = "complete";
    state.deadline = null;
    state.currentTile = null;
    state.winnerIds = remaining.map((candidate) => candidate.playerId);
    state.finalReason = reason;
    return;
  }
  if (index === state.activePlayerIndex && state.currentTile) {
    state.discardedTiles.push(state.currentTile);
    state.currentTile = null;
    advanceActivePlayer(state);
    drawPlaceableTile(state);
  }
  if (state.phase === "complete") {
    const events: ScoreEvent[] = [];
    finishByTiles(state, events);
  } else {
    state.deadline = now + TURN_TIMEOUT_MS;
  }
}

function advanceActivePlayer(state: LunaState): void {
  for (let step = 1; step <= state.players.length; step++) {
    const index = (state.activePlayerIndex + step) % state.players.length;
    if (!state.players[index]!.forfeited) {
      state.activePlayerIndex = index;
      return;
    }
  }
}

function componentFromNode(state: BoardFeatureState, startNode: string): FeatureComponent {
  const start = regionAtNode(state, startNode);
  const type = start.region.type;
  if (type === "solar") {
    return {
      type,
      nodes: [startNode],
      tileKeys: [start.tileKey],
      complete: occupiedSolarNeighbors(state, start.tileKey) === 8,
      launchpads: 0,
    };
  }
  const pending = [startNode];
  const visited = new Set<string>();
  let complete = true;
  let launchpads = 0;
  while (pending.length > 0) {
    const currentNode = pending.pop()!;
    if (visited.has(currentNode)) continue;
    visited.add(currentNode);
    const current = regionAtNode(state, currentNode);
    if (current.region.launchpad) launchpads += 1;
    const coordinate = parseCoordinate(current.tileKey);
    for (const direction of rotatedRegionEdges(current.tile.tileId, current.region.id, current.tile.rotation)) {
      const delta = DELTAS[direction];
      const neighborKey = coordinateKey(coordinate.x + delta.x, coordinate.y + delta.y);
      const neighbor = state.board[neighborKey];
      if (!neighbor) {
        complete = false;
        continue;
      }
      const neighborRegion = TILE_CATALOG[neighbor.tileId].regions.find((region) =>
        region.type === type && rotatedRegionEdges(neighbor.tileId, region.id, neighbor.rotation).includes(OPPOSITE[direction]),
      );
      if (!neighborRegion) throw new Error("invalid_board_connectivity");
      pending.push(nodeKey(neighborKey, neighborRegion.id));
    }
  }
  return {
    type,
    nodes: [...visited].sort(),
    tileKeys: [...new Set([...visited].map((node) => parseNode(node).tileKey))].sort(),
    complete,
    launchpads,
  };
}

function regionAtNode(state: Pick<LunaState, "board">, node: string) {
  const { tileKey, regionId } = parseNode(node);
  const tile = state.board[tileKey];
  const region = tile && TILE_CATALOG[tile.tileId].regions.find((candidate) => candidate.id === regionId);
  if (!tile || !region) throw new Error("unknown_feature_node");
  return { tileKey, tile, region };
}

function claimsFor(state: Pick<LunaState, "claims">, component: FeatureComponent): LunaClaim[] {
  const nodes = new Set(component.nodes);
  return state.claims.filter((claim) => nodes.has(claim.node));
}

function featureKey(component: FeatureComponent): string {
  return `${component.type}:${component.nodes.join(";")}`;
}

function occupiedSolarNeighbors(state: Pick<LunaState, "board">, tileKey: string): number {
  const { x, y } = parseCoordinate(tileKey);
  let occupied = 0;
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    if ((dx !== 0 || dy !== 0) && state.board[coordinateKey(x + dx, y + dy)]) occupied += 1;
  }
  return occupied;
}

/** Server-side sanity check for persisted JSON before it is trusted. */
export function validateLunaState(value: unknown): LunaState {
  if (!value || typeof value !== "object") throw new Error("invalid_state");
  const state = value as Partial<LunaState>;
  if (
    state.schemaVersion !== 1 || state.rulesVersion !== LUNA_RULES_VERSION || typeof state.roomId !== "string"
    || !Number.isInteger(state.capacity) || !Array.isArray(state.players) || !state.board || typeof state.board !== "object"
    || !Array.isArray(state.claims) || !Array.isArray(state.tileStack) || !Array.isArray(state.discardedTiles)
    || !Array.isArray(state.settledFeatures) || !Array.isArray(state.winnerIds) || !Number.isInteger(state.revision)
  ) throw new Error("invalid_state");
  return structuredClone(state as LunaState);
}
